<template>
  <tr :class="{ 'v-data-table__selected': isSelected }">
    <td>
      <v-checkbox
        :model-value="isSelected"
        @update:model-value="$emit('select:task', item._key)"
      />
    </td>
    <td>
      <v-tooltip location="bottom">
        <template #activator="{ props }">
          <div
            class="dot cursor-pointer"
            :class="taskDotInfo.class"
            v-bind="props"
          />
        </template>
        <span>{{ taskDotInfo.text }}</span>
      </v-tooltip>
    </td>
    <td class="task-title pl-0">
      <b class="text-primary cursor-pointer" @click="$emit('edit:task')">
        <strong>{{ item.title }}</strong>
      </b>
    </td>
    <td class="date pl-0">{{ formattedDueDate }}</td>
    <td class="date pl-0"><MarketingPushChip v-if="isValuePresent(item.priority)" :value="item.priority" /></td>
    <td class="date pl-0">
      {{ $filters.formatJSDateTime(item.created.date) }}
    </td>
    <td class="pl-0">{{ item.created.name }}</td>
    <td class="pl-0">
      {{ assignedToLabel }}
    </td>
    <td class="pl-0">{{ taskTypesLabels[item.type] }}</td>
    <td class="date pl-0">
      {{ $filters.capitalize(item.status) }}
      <span v-if="item.status === taskStatusEnum.completed">
        <br>
        <template v-if="item.completed_by">
          {{ item.completed_by.name }}<br>
        </template>
      </span>
    </td>
    <td class="pl-0">
      <v-card
        v-if="item.collections.length"
        variant="flat"
        color="transparent"
        class="task-collection"
      >
        <v-list>
          <CollectionTile
            v-for="collectionValue in item.collections"
            :key="collectionValue._edge"
            :collection="collectionValue"
          />
        </v-list>
      </v-card>
      <span v-else class="na">No related records</span>
    </td>
  </tr>
</template>

<script lang="ts">
import { PropType, defineComponent } from 'vue';
import { DateTime } from 'luxon';

import { ITask } from '@/bundles/Tasks/interfaces/ITask';
import { TaskStatusEnum } from '@/bundles/Tasks/enums/TaskStatusEnum';
import { TaskTypesLabels } from '@/bundles/Tasks/enums/TaskTypesEnum';
import { isValuePresent } from '@/bundles/App/helpers/isValuePresent';

import CollectionTile from '@/bundles/Tasks/components/CollectionTile.vue';
import MarketingPushChip from '@/bundles/Common/components/marketing/MarketingPushChip.vue';

export default defineComponent({
  name: 'TaskTableContentItem',

  components: { MarketingPushChip, CollectionTile },

  props: {
    item: {
      type: Object as PropType<ITask>,
      required: true,
    },
    isSelected: Boolean,
  },

  emits: ['edit:task', 'select:task'],

  computed: {
    taskStatusEnum (): typeof TaskStatusEnum {
      return TaskStatusEnum;
    },
    taskTypesLabels (): typeof TaskTypesLabels {
      return TaskTypesLabels;
    },
    assignedToLabel (): string {
      if (this.item.assignee_info) {
        return this.item.assignee_info.name;
      }
      return 'N/A';
    },
    taskDotInfo (): { text: string; class: string } {
      if (this.item.status === TaskStatusEnum.completed) {
        return { text: 'Completed', class: 'completed' };
      }

      const dateDue = DateTime.fromJSDate(this.item.due_date);
      const currentDate = DateTime.now();

      const isExpired = dateDue < currentDate && this.item.status !== TaskStatusEnum.completed;
      const isExpiring = dateDue < currentDate.plus({ hours: 7 }) && dateDue > currentDate && this.item.status !== TaskStatusEnum.completed;

      if (isExpiring) {
        return { text: 'Expiring soon', class: 'expiring' };
      }

      if (isExpired) {
        return { text: 'Expired', class: 'expired' };
      }

      return { text: 'Active', class: '' };
    },
    formattedDueDate (): string {
      return DateTime.fromJSDate(this.item.due_date).toLocaleString(DateTime.DATETIME_SHORT);
    },
  },

  methods: {
    isValuePresent
  }
});
</script>

<style scoped>
.dot {
  height: 20px;
  width: 20px;
  border-radius: 100%;
  background-color: #38b714;
}
.expired {
  background-color: #d0021b;
}
.expiring {
  background-color: #f8e71c;
}
.completed {
  background-color: #d8d8d8;
}
</style>
